// Finance API client. Read-only, mirrors ProjectsService's auth pattern.
// The /finance/investments payload is already card-shaped (see the INVEST
// object in serga-os/data.js) so no mapping layer is needed.

const API_BASE_URL = "https://api.serga.ba";

export interface InvestmentsView {
  currency: string;
  total: number;
  asof: string;
  period: string;
  periods: string[];
  delta: Record<string, { abs: number; pct: number }>;
  holdings: Array<{
    sym: string;
    name: string;
    qty: number | null;
    value: number;
    d: Record<string, number>;
  }>;
  totalPL?: { abs: number; pct: number };
  partial?: boolean;
}

export interface BudgetView {
  month: string;
  currency: string;
  months: string[];
  totals: { spent: number; income: number; net: number };
  categories: Array<{
    id: string;
    label: string;
    group?: string;
    budget: number;
    spent: number;
  }>;
  transactions: Array<{
    date: string;
    desc: string;
    amount: number;
    category: string;
    source?: string;
  }>;
}

export class FinanceService {
  private static authTokens: any = null;

  static setAuthTokens(tokens: any) {
    this.authTokens = tokens;
  }

  private static getAuthHeaders() {
    if (!this.authTokens?.idToken) {
      throw new Error("No authentication token available");
    }
    return {
      Authorization: `Bearer ${this.authTokens.idToken}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Live portfolio view, or null when the backend has no snapshot yet
   * ({ pending: true }) so the caller keeps its fixture.
   */
  static async getInvestments(): Promise<InvestmentsView | null> {
    const res = await fetch(`${API_BASE_URL}/finance/investments`, {
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    const data = await res.json();
    if (!data || data.pending) return null;
    return data as InvestmentsView;
  }

  /**
   * Monthly budget view, or null when no statement has been imported yet
   * ({ pending: true }) so the caller keeps its fixture.
   */
  static async getBudget(month?: string): Promise<BudgetView | null> {
    const query = month ? `?month=${encodeURIComponent(month)}` : "";
    const res = await fetch(`${API_BASE_URL}/finance/budget${query}`, {
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    const data = await res.json();
    if (!data || data.pending) return null;
    return data as BudgetView;
  }

  private static async send(path: string, method: string, body: any) {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      let msg = `Request failed: ${res.status}`;
      try {
        const p = JSON.parse(text);
        if (p.error) msg = p.error;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  }

  // ======== Writes (SHELL editor) ========

  static async getHoldings(): Promise<{ positions: any[] }> {
    const res = await fetch(`${API_BASE_URL}/finance/investments/holdings`, {
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return res.json();
  }

  static putHoldings(positions: any[]): Promise<any> {
    return this.send("/finance/investments/holdings", "PUT", { positions });
  }

  static putBudgetCategories(categories: any[]): Promise<any> {
    return this.send("/finance/budget/categories", "PUT", { schemaVersion: 1, categories });
  }

  static putBudgetMonth(month: string, doc: any): Promise<any> {
    return this.send(`/finance/budget/${month}`, "PUT", doc);
  }
}
